const MAX_ISSUES_SHOWN = 3;
const MAX_ISSUES_LENGTH = 300;

const ROW_ISSUE_PATTERN = /^(.*?)Row (\d+), (.+)$/;

type GroupedIssue = {
  prefix: string;
  text: string;
  rows: string[];
};

const groupIssuesByText = (issueParts: string[]): GroupedIssue[] => {
  const grouped = new Map<string, GroupedIssue>();

  for (const part of issueParts) {
    const match = part.match(ROW_ISSUE_PATTERN);
    const prefix = match ? match[1] : '';
    const row = match ? match[2] : null;
    const text = match ? match[3] : part;

    const existing = grouped.get(text);
    if (existing) {
      if (row) existing.rows.push(row);
      if (prefix.length > existing.prefix.length) existing.prefix = prefix;
    } else {
      grouped.set(text, { prefix, text, rows: row ? [row] : [] });
    }
  }

  return Array.from(grouped.values());
};

const renderIssue = (issue: GroupedIssue): string => {
  if (issue.rows.length === 0) return `${issue.prefix}${issue.text}`;
  if (issue.rows.length === 1) {
    return `${issue.prefix}Row ${issue.rows[0]}, ${issue.text}`;
  }
  return `${issue.prefix}${issue.text} (${issue.rows.length} rows)`;
};

const truncateIssues = (errorMessage: string): string => {
  const grouped = groupIssuesByText(errorMessage.split('; '));
  const shown = grouped.slice(0, MAX_ISSUES_SHOWN);

  let text = shown.map(renderIssue).join('; ');
  if (text.length > MAX_ISSUES_LENGTH) {
    text = `${text.slice(0, MAX_ISSUES_LENGTH)}…`;
  }

  const hiddenCount = grouped.length - shown.length;
  if (hiddenCount > 0) {
    text += ` … and ${hiddenCount} more ${hiddenCount === 1 ? 'issue' : 'issues'}`;
  }

  return text;
};

type FailedUpload = {
  fileName: string;
  errorMessage?: string;
};

export const formatFailedUploadsMessage = (
  failedUploads: FailedUpload[],
): string =>
  failedUploads
    .map((failed) =>
      failed.errorMessage
        ? `${failed.fileName}: ${truncateIssues(failed.errorMessage)}`
        : `${failed.fileName}: upload failed. Please try again.`,
    )
    .join(' — ');
